/**
 * Реестр индикаторов терминала (ТЗ, раздел 4.3).
 *
 * Важно, чем это НЕ является. Индикаторы терминала — инструмент
 * визуального анализа пользователя. Они не участвуют в генерации сигналов
 * и не связаны с ботами: бот, которому нужна EMA, считает её сам, внутри
 * своего кода и своей версии. Иначе правка отрисовки в терминале молча
 * меняла бы сигналы работающего бота.
 *
 * Тот же механизм позже примет пользовательские индикаторы: определение
 * описывает себя само — параметры, выходы, размещение, — и добавление
 * нового не требует правок графика.
 */

import type { DerivativePoint, Timeframe } from '@specter/types';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import type { OrderFlowData } from '../orderflow/data.js';
import type { OrderBookData } from '../orderflow/book-data.js';

/** Куда рисовать индикатор. */
export type IndicatorPlacement =
  /** Поверх цены. */
  | 'overlay'
  /** На панель объёма. */
  | 'volume'
  /** В собственную панель снизу. */
  | 'pane';

export interface NumberParamSpec {
  readonly key: string;
  readonly label: string;
  /**
   * Объяснение человеческим языком: что это и на что влияет.
   *
   * Подпись настройки называет величину, но не отвечает на вопрос, зачем
   * её трогать. «Окно вершины, свечей» — точное имя и пустой звук для
   * того, кто видит индикатор впервые.
   *
   * Пишется как ответ, а не как определение: что произойдёт, если
   * увеличить, и чем за это платят. Показывается по наведению на саму
   * подпись — та подчёркнута пунктиром, и это единственный знак, что
   * объяснение есть.
   */
  readonly hint?: string;
  readonly type: 'number';
  readonly default: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
}

export interface SourceParamSpec {
  readonly key: string;
  readonly label: string;
  /** Объяснение человеческим языком — см. `NumberParamSpec.hint`. */
  readonly hint?: string;
  readonly type: 'source';
  readonly default: PriceSource;
}

/**
 * Выбор из закрытого списка.
 *
 * Отличается от источника цены тем, что список задаёт сам индикатор:
 * источник у всех один и тот же, а «тип средней» или «метод расчёта» —
 * свои у каждого.
 */
export interface SelectParamSpec {
  readonly key: string;
  readonly label: string;
  /** Объяснение человеческим языком — см. `NumberParamSpec.hint`. */
  readonly hint?: string;
  readonly type: 'select';
  readonly default: string;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
}

/**
 * Цвет как настройка индикатора.
 *
 * Не то же, что цвет выхода. Цвет выхода принадлежит серии и живёт в
 * `colors` экземпляра; здесь цвет — часть расчёта фигур, у которых серии
 * нет вовсе. У карты ликвидности две стороны, и человек красит их
 * порознь, хотя ни одной линии на графике у неё не появляется.
 */
export interface BooleanParamSpec {
  readonly key: string;
  readonly label: string;
  /** Объяснение человеческим языком — см. `NumberParamSpec.hint`. */
  readonly hint?: string;
  readonly type: 'boolean';
  readonly default: boolean;
}

export interface ColorParamSpec {
  readonly key: string;
  readonly label: string;
  /** Объяснение человеческим языком — см. `NumberParamSpec.hint`. */
  readonly hint?: string;
  readonly type: 'color';
  readonly default: string;
}

/**
 * Дни недели.
 *
 * Хранится строкой цифр по ISO: `1` — понедельник, `7` — воскресенье;
 * `'12345'` — будни. Строка, а не семь галочек: семь строк «показывать
 * понедельник» заняли бы пол-окна, а ряд кнопок читается одним взглядом.
 */
export interface DaysParamSpec {
  readonly key: string;
  readonly label: string;
  /** Объяснение человеческим языком — см. `NumberParamSpec.hint`. */
  readonly hint?: string;
  readonly type: 'days';
  readonly default: string;
  /** Дни, которые выбрать нельзя, с объяснением почему. */
  readonly locked?: { readonly days: string; readonly reason: string };
}

export type IndicatorParamSpec =
  | NumberParamSpec
  | SourceParamSpec
  | SelectParamSpec
  | ColorParamSpec
  | BooleanParamSpec
  | DaysParamSpec;

/** Выбранные дни недели: номера по ISO, 1 — понедельник. */
export function daysParam(params: IndicatorParams, key: string, fallback: string): Set<number> {
  const value = params[key];
  const text = typeof value === 'string' ? value : fallback;
  return new Set([...text].map(Number).filter((day) => day >= 1 && day <= 7));
}

export type PriceSource = 'close' | 'open' | 'high' | 'low' | 'hl2' | 'ohlc4';

export type IndicatorParams = Readonly<Record<string, number | string | boolean>>;

export interface IndicatorOutput {
  readonly key: string;
  readonly label: string;
  readonly type: 'line' | 'histogram';
  readonly defaultColor: string;
  /** Толщина линии по умолчанию. */
  readonly lineWidth?: 1 | 2 | 3 | 4;
  /**
   * Как подписывать значения на шкале.
   *
   * `volume` — компактно (1.2K, 3.4M): объём в сотнях тысяч единиц
   * растянул бы шкалу цены до нечитаемости.
   *
   * `money` — деньги со знаком: «−$265,3 млн». Перевес покупок в
   * долларах с копейками — «−265 293 691,78» — это задача на устный
   * счёт, а не ответ.
   */
  readonly format?: 'price' | 'volume' | 'money';
  /**
   * Своя панель под графиком, со своей видимой шкалой.
   *
   * Для выхода, чьи значения другого порядка, чем у соседей: накопленная
   * дельта доходит до миллиардов, а столбики свечи — десятки миллионов.
   * На общей шкале столбики сжимались в полоску у нуля, а линия уходила
   * за край; на двух шкалах в одной панели нули стояли на разной высоте
   * и казалось, что ряды расходятся. В своей панели у каждого своя
   * шкала с цифрами, и читать её можно без оговорок.
   */
  readonly ownPane?: boolean;
  /**
   * Столбики — цветом выхода, а не по знаку.
   *
   * Обычно гистограмма красится по знаку: выше нуля зелёная, ниже —
   * красная. Но когда знак — это сторона, а не «хорошо/плохо», так
   * выходит наоборот: у ликвидаций вынесенные лонги стоят вверху, и
   * по знаку они окрашивались зелёным, хотя в легенде — красные.
   */
  readonly solid?: boolean;
}

/** Горизонтальный уровень в панели индикатора: 70/30 у RSI, 0 у MACD. */
export interface IndicatorLevel {
  readonly value: number;
  readonly color: string;
  readonly dashed?: boolean;
}

export interface IndicatorScale {
  readonly min?: number;
  readonly max?: number;
  readonly levels?: readonly IndicatorLevel[];
}

/**
 * Точка ряда. `null` — прогрев ещё не закончился, рисовать нечего.
 *
 * `color` задаётся, только когда цвет несёт смысл на каждом баре:
 * столбец объёма красится по направлению свечи, а не по знаку значения.
 */
export type IndicatorPoint = {
  readonly time: number;
  readonly value: number | null;
  readonly color?: string;
};

export type IndicatorResult = Readonly<Record<string, readonly IndicatorPoint[]>>;

/**
 * Чей индикатор.
 *
 * `base` — общеизвестная классика: EMA, RSI, MACD. Их знает любой
 * терминал, и человек ищет их по привычному имени.
 *
 * `specter` — собранное нами. Разделены они не ради марки на экране, а
 * потому что человек по-разному их выбирает: за RSI приходят зная, что
 * это, а за авторским — прочитав, что оно делает. Поэтому у второго и
 * описание длиннее, и место отдельное.
 */
export type IndicatorAuthor = 'base' | 'specter';

/**
 * Чужие ряды, которые индикатор попросил.
 *
 * Пересобрать старший таймфрейм из открытого можно, но недалеко: шестьсот
 * четырёхчасовых баров — это почти десять тысяч пятнадцатиминутных, а
 * загружено две тысячи. Младший не собрать вовсе. Поэтому ряд берётся у
 * Core — там он есть целиком.
 *
 * `null` означает «не приехал», и это не то же самое, что «пусто»:
 * индикатор обязан промолчать, а не посчитать по тому, что под рукой.
 */
/*
 * `spotDelta` — перевес покупок над продажами по рынку на споте Binance
 * по барам графика. Из него индикатор Spot CVD копит свою линию.
 */
export type DerivativeKind =
  | 'oi'
  | 'funding'
  | 'premium'
  | 'spotDelta'
  | 'futuresDelta'
  // Доля покупок по рынку в обороте бара (контракты Binance) — для «Выгодных зон PRO 2».
  | 'futuresBuyShare'
  // Накопленные сервером: доля счетов и крупных в длинных, оценка Market Intelligence.
  | 'longAccounts'
  | 'topPositions'
  | 'miScore'
  // Ликвидации всего рынка по барам: длинные, короткие, сколько позиций.
  | 'liqLong'
  | 'liqShort'
  | 'liqCount';

export interface IndicatorContext {
  series(timeframe: Timeframe): readonly Candle[] | null;
  /**
   * Производный ряд по открытому инструменту: открытый интерес, ставка
   * финансирования, премия. Пусто — биржа его не отдаёт.
   */
  derivative(kind: DerivativeKind): readonly DerivativePoint[];
  /** Рыночный агрегат: `TOTAL`, `USDT.D`, `USDT.MC`. */
  index(symbol: string): readonly Candle[];
  /** Последний бар ряда ещё набирается. */
  readonly livePartial: boolean;
  /**
   * Производные ряды доехали.
   *
   * Свечи приходят раньше открытого интереса, и в это окно расчёт
   * видит пустой ряд. Для счёта это не «ноль», а «данных нет»: блоки
   * интереса выпадают из знаменателя, счёт собирается из оставшихся и
   * выходит завышенным. Метка появлялась, а через секунду исчезала —
   * человек видел сигнал, который «пропал».
   *
   * Пока ряд едет, метки не рисуются вовсе: показать и отобрать хуже,
   * чем показать на секунду позже.
   */
  readonly derivativesReady: boolean;
  /** Таймфрейм графика: по нему индикатор просит собственную глубину. */
  readonly timeframe: Timeframe;
  /**
   * Кому принадлежат эти ряды: биржа и монета.
   *
   * Индикатор вправе помнить свой разбор между вызовами — `shapes` и
   * `readout` зовутся подряд с тем же рядом, и считать дважды незачем.
   * Но узнать по самому ряду, что монета сменилась, нельзя: у SOL и ETH
   * одинаковое число баров и одно и то же время последнего. Без имени
   * инструмента память отдаёт разбор прошлой монеты новой, и держится
   * это до перезагрузки страницы.
   */
  readonly instrument: string;
  /**
   * Поток ордеров по инструменту: сделки по уровням цены.
   *
   * Один и тот же объект, пока не сменились инструмент и таймфрейм: он
   * меняется на месте четыре раза в секунду, и пересчитывать ради этого
   * все индикаторы незачем. Рисующий его слой подписан на изменения сам.
   * Пусто — поток никто не просил.
   */
  readonly orderFlow?: OrderFlowData | null;
  /** Стакан по инструменту: средние заявки по барам и стакан сейчас. */
  readonly orderBook?: OrderBookData | null;
}

export interface IndicatorDefinition {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Пусто — классика: так короче в определениях, которых большинство. */
  readonly author?: IndicatorAuthor;
  readonly placement: IndicatorPlacement;
  readonly params: readonly IndicatorParamSpec[];
  readonly outputs: readonly IndicatorOutput[];
  readonly scale?: IndicatorScale;
  /**
   * Таймфреймы того же инструмента, без которых индикатор неполон.
   *
   * Объявляются, а не запрашиваются: терминал грузит ровно названное и
   * ровно тогда, когда индикатор стоит на графике.
   */
  readonly timeframes?: readonly Timeframe[];
  /**
   * На каких таймфреймах графика догружать глубокий ряд своего же
   * таймфрейма — как это делают «Выгодные зоны PRO» через `timeframes`.
   *
   * Для индикатора, которому нужна та же глубина, что у зон, но не их
   * пять соседних таймфреймов: грузится только ряд открытого графика и
   * только если он в списке.
   */
  readonly deepOn?: readonly Timeframe[];
  /**
   * Старшие таймфреймы, которые нужны при этих настройках.
   *
   * Как `timeframes`, но по выбору человека: имбалансы грузят 4ч ряд,
   * только если человек отметил «4ч». Держать загруженными все пять
   * рядов по пять тысяч свечей ради одной галочки — расточительно.
   */
  timeframesOf?(params: IndicatorParams, chart: Timeframe): readonly Timeframe[];
  /** Производные ряды, которые индикатор просит по своему инструменту. */
  readonly derivatives?: readonly DerivativeKind[];
  /** Рыночные агрегаты, которые индикатор просит. */
  readonly indices?: readonly string[];
  /** Индикатору нужны сделки по уровням цены. */
  readonly orderFlow?: boolean;
  /** Индикатору нужен стакан. */
  readonly orderBook?: boolean;
  /**
   * Без чего на сервере индикатор пуст.
   *
   * Этап 3 потока ордеров включается на сервере отдельно; пока его нет,
   * такие индикаторы не показываются в списке.
   */
  readonly requires?: 'liquidations' | 'book';
  /** Сколько баров нужно до первого достоверного значения. */
  warmup(params: IndicatorParams): number;
  calc(
    candles: readonly Candle[],
    params: IndicatorParams,
    context: IndicatorContext,
  ): IndicatorResult;
  /**
   * Фигуры вместо ряда во времени.
   *
   * Есть индикаторы, у которых нет значения «на баре»: у профиля объёма
   * вес принадлежит ценовому уровню, у карты ликвидности — отрезку
   * времени. Отдельный метод, а не хитрый выход `calc`, потому что это
   * другая величина, и притворять её рядом пришлось бы всем — и графику,
   * и легенде, и тестам.
   */
  shapes?(
    candles: readonly Candle[],
    params: IndicatorParams,
    context: IndicatorContext,
  ): readonly IndicatorShape[];
  /**
   * Табло: несколько строк поверх графика.
   *
   * Не всё, что индикатор знает, выражается линией. «Глубоко под Лонг» и
   * «до ближайшей ликвидности сверху 1.2%» — это ответы словами, и
   * рисовать их некуда, кроме как написать.
   */
  readout?(
    candles: readonly Candle[],
    params: IndicatorParams,
    context: IndicatorContext,
  ): readonly ReadoutRow[];
  /**
   * Что означает вид фигур этого индикатора на графике.
   *
   * Поле, а не метод: от настроек оно не зависит — пунктир означает
   * скрытое расхождение при любом пороге. Пусто у тех, у кого линия
   * одна и объяснять в ней нечего.
   */
  readonly legend?: readonly ReadoutLegendItem[];
  /** Подпись в легенде: EMA (21), BB (20, 2). */
  label(params: IndicatorParams): string;
}

/** Настроенный пользователем экземпляр индикатора на графике. */
export interface IndicatorInstance {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly params: IndicatorParams;
  /** Цвета по ключам выходов; пусто — берётся defaultColor. */
  readonly colors: Readonly<Record<string, string>>;
  readonly visible: boolean;
  /**
   * Доля высоты графика под собственной панелью, если её тянули.
   *
   * Доля, а не пиксели, и это не мелочь. Панели графика живут
   * пропорциями: высота, заданная в пикселях, тут же пересчитывается в
   * долю от того, каким график был **в этот момент**. А панели
   * создаются раньше, чем контейнер получает окончательный размер, —
   * записанные двести двадцать пикселей возвращались четырьмястами.
   *
   * Хранится у экземпляра, а не отдельным списком по номерам панелей:
   * номер меняется, стоит убрать соседний индикатор, и высота досталась
   * бы чужой панели. Пусто — панель берёт долю по умолчанию.
   *
   * У индикаторов поверх цены смысла не имеет: своей панели у них нет.
   */
  readonly paneShare?: number;
}

export function priceOf(candle: Candle, source: PriceSource): number {
  switch (source) {
    case 'open':
      return candle.open;
    case 'high':
      return candle.high;
    case 'low':
      return candle.low;
    case 'hl2':
      return (candle.high + candle.low) / 2;
    case 'ohlc4':
      return (candle.open + candle.high + candle.low + candle.close) / 4;
    case 'close':
    default:
      return candle.close;
  }
}

export const SOURCE_LABELS: Record<PriceSource, string> = {
  close: 'Close',
  open: 'Open',
  high: 'High',
  low: 'Low',
  hl2: '(H+L)/2',
  ohlc4: '(O+H+L+C)/4',
};

export function numberParam(params: IndicatorParams, key: string, fallback: number): number {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function sourceParam(params: IndicatorParams, key: string): PriceSource {
  const value = params[key];
  return typeof value === 'string' && value in SOURCE_LABELS
    ? (value as PriceSource)
    : 'close';
}

/**
 * Строковый параметр из закрытого списка.
 *
 * Возвращает умолчание, если в профиле лежит значение, которого в
 * списке больше нет: настройки живут дольше кода.
 */
/** Галочка из настроек. */
export function boolParam(params: IndicatorParams, key: string, fallback: boolean): boolean {
  const value = params[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** Цвет из настроек; мусор из сохранённого профиля заменяется умолчанием. */
export function colorParam(params: IndicatorParams, key: string, fallback: string): string {
  const value = params[key];
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

export function selectParam(
  params: IndicatorParams,
  key: string,
  fallback: string,
): string {
  const value = params[key];
  return typeof value === 'string' ? value : fallback;
}
